import { GridColumns } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";
import Table from "../../../reusable-components/Table/Table";
import { FT_PT } from "../../../utils/constants/filter-tables-constants";
import { RecruitersApiService } from "../../../services/recruiting/recruiters/recruiters-api.service";
import { PositionsApiService } from "../../../services/recruiting/positions/positions-api.service";
import { PositionDTO } from "../../../models/recruiting/positions/position-dto";
import { HiringClientsApiService } from "../../../services/recruiting/hiring-clients/hiring-clients-api.service";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { AddButton, AddButtonCv, Container, SkillsContainer, SkillsBlock, StatusCvContainer } from "./styles";
import EditCvStatus from "./EditCvStatus/EditCvStatus";
import { renderCellExpand } from "../../../reusable-components/RenderCellExpand/RenderCellExpand";
import { PropsFilter } from "../../../models/api/props-filter/props-filter";
import { useStatusQuickFilter } from "../../../containers/Quickfilters/StatusesQuickfilter/useStatusQuickFilter";
import { StatusQuickfilter } from "../../../containers/Quickfilters/StatusesQuickfilter/StatusQuickfilter";
import { usePositionDateQuickFilter } from "../../../containers/Quickfilters/PositionDateQuickfilter/usePositionDateQuickFilter";
import QuickfiltersContainer, {
  FiltersContainer,
} from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { PositionDateQuickfilter } from "../../../containers/Quickfilters/PositionDateQuickfilter/PositionDateQuickfilter";
import { PositionWithoutActivityQuickfilter } from "../../../containers/Quickfilters/PositionWithoutActivityQuickfilter/PositionWithoutActivityQuickfilter";
import { usePositionWithoutActivityQuickFilter } from "../../../containers/Quickfilters/PositionWithoutActivityQuickfilter/usePositionWithoutActivityQuickFilter";
import PositionSkillsDetailsModal from "./PositionSkillsDetailsModal/PostionSkillsDetailsModal";
import { SKILLS_CATEGORIES } from "../../../utils/constants/constants";
import { CountriesApiService } from "../../../services/countries/countries-api.service";
import { PositionStatusesApiService } from "../../../services/recruiting/positions/position-statuses/position-statuses-api.service";
import { PositionStatusRef } from "../../../models/recruiting/positions/position-status/position-status-ref";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { SkillCategoryRef } from "../../../models/recruiting/candidate/skills/skill-category/skill-category-ref";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { Button, Tooltip, Typography, useTheme } from "@mui/material";
import CommentBubble from "../../../reusable-components/CommentBubble/CommentBubble";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AddEditRequiredPositionSkills } from "./AddEditRequiredPositionSkills/AddEditRequiredPositionSkills";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { CandidateRef } from "../../../models/recruiting/candidate/candidate-ref";
import { RecruiterRef } from "../../../models/recruiting/recruiters";
import { CountryRef } from "../../../models/countries";
import { ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";

const Positions = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.open_positions";
  const translationFields = `${tRP}.fields`;

  const theme = useTheme();
  const { forceUpdate } = useForceUpdate();

  const isAllowedEditAddDelete = useAuthorization([
    AUTHORIZATIONS.RecruitingAccess.CanOpenPositionsAndAddCreateCandidates,
  ]);

  const statusCollectionRef = useRef<PositionStatusRef[]>([]);

  const { positionDateQuickFilter, setPositionDateQuickFilter } = usePositionDateQuickFilter(true);

  const { statusQuickFilter, setStatusQuickFilter } = useStatusQuickFilter(true, setPositionDateQuickFilter);

  const { positionWithoutActivityQuickFilter, setPositionWithoutActivityQuickFilter } =
    usePositionWithoutActivityQuickFilter();

  const futureDay = new Date();
  futureDay.setDate(futureDay.getDate() + 30);

  const setCvStatuses = useCallback(async () => {
    try {
      const responce = await new PositionStatusesApiService().loadItems();

      if (!responce.error) {
        statusCollectionRef.current = responce.items;
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    setCvStatuses();
  }, [setCvStatuses]);

  const PositionContainer = (row: PositionDTO) => (
    <ConcatenatedCell
      aligned={"left"}
      FirstElement={
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span title={row?.positionCode ?? ""}>{row?.positionCode}</span>
          {row?.description && (
            <CommentBubble
              title={`${t(`${translationFields}.description`)}`}
              comment={row?.description}
            />
          )}
        </Grid>
      }
      SecondElement={
        <Grid>
          <Typography
            style={{ color: theme.palette.text.disabled }}
            variant={"body2"}
          >
            {row?.position}
          </Typography>
        </Grid>
      }
    />
  );

  const PositionCellMemoizedRenderer = React.memo(PositionContainer, (prevProps, nextProps) => {
    return (
      prevProps?.position === nextProps?.position &&
      prevProps?.description === nextProps?.description &&
      prevProps?.positionCode === nextProps?.positionCode
    );
  });

  const HiringClientContainer = (row: PositionDTO) => (
    <ConcatenatedCell
      aligned={"left"}
      FirstElement={
        <Grid>
          <Typography title={`${row?.hiringClient?.name ?? ""}(${row?.hiringClient?.owner?.name ?? ""})`}>
            {`${row?.hiringClient?.name}(${row?.hiringClient?.owner?.name ?? ""})`}
          </Typography>
        </Grid>
      }
      SecondElement={
        <Grid>
          <Typography
            style={{ color: theme.palette.text.disabled }}
            variant={"body2"}
            title={row?.clientContact ?? ""}
          >
            {row?.clientContact}
          </Typography>
        </Grid>
      }
    />
  );

  const HiringClientCellMemoizedRenderer = React.memo(HiringClientContainer, (prevProps, nextProps) => {
    return prevProps?.hiringClient === nextProps?.hiringClient && prevProps?.clientContact === nextProps?.clientContact;
  });

  const DateStatus = (row: PositionDTO) => (
    <Grid title={`Created at: ${intlFormatDateShort(row.createdAt)}`}>
      {row?.isOpen ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: "bold" }}>Open until:</span>
          <span>{intlFormatDateShort(row.openUntil as Date)}</span>
        </div>
      ) : (
        <span style={{ fontWeight: "bold" }}>Closed</span>
      )}
    </Grid>
  );

  const DateStatusCellMemoizedRenderer = React.memo(DateStatus, (prevProps, nextProps) => {
    return prevProps?.isOpen === nextProps?.isOpen && prevProps?.openUntil === nextProps?.openUntil;
  });

  const SentCvToClient = (row: PositionDTO) => (
    <StatusCvContainer>
      {row?.cVsSentToClientInProgress && row?.cVsSentToClientDeclined && (
        <Container>
          <div>
            {row.cVsSentToClientInProgress.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsSentToClientInProgress as CandidateRef[]}
                currCollectionName={"cVsSentToClientInProgress"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
          <div>
            {row.cVsSentToClientDeclined.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsSentToClientDeclined as CandidateRef[]}
                currCollectionName={"cVsSentToClientDeclined"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  style={{ color: theme.palette.error.main }}
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
        </Container>
      )}
      <AddButtonCv>
        <EditCvStatus
          currPosition={row}
          currCollection={row.cVsSentToClientInProgress as CandidateRef[]}
          currCollectionName={"cVsSentToClientInProgress"}
          statusCollectionRef={statusCollectionRef.current}
          notifyParent={forceUpdate}
        />
      </AddButtonCv>
    </StatusCvContainer>
  );

  const SentCvToClientCellMemoizedRenderer = React.memo(SentCvToClient, (prevProps, nextProps) => {
    return (
      prevProps?.cVsSentToClientInProgress === nextProps?.cVsSentToClientInProgress &&
      prevProps?.cVsSentToClientDeclined === nextProps?.cVsSentToClientDeclined
    );
  });

  const CvInterviewedByClient = (row: PositionDTO) => (
    <StatusCvContainer>
      {row?.cVsInterviewedByClientInProgress && row?.cVsInterviewedByClientDeclined && (
        <Container>
          <div>
            {row.cVsInterviewedByClientInProgress.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsInterviewedByClientInProgress as CandidateRef[]}
                currCollectionName={"cVsInterviewedByClientInProgress"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
          <div>
            {row.cVsInterviewedByClientDeclined.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsInterviewedByClientDeclined as CandidateRef[]}
                currCollectionName={"cVsInterviewedByClientDeclined"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  style={{ color: theme.palette.error.main }}
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
        </Container>
      )}
      <AddButtonCv>
        <EditCvStatus
          currPosition={row}
          currCollection={row.cVsInterviewedByClientInProgress as CandidateRef[]}
          currCollectionName={"cVsInterviewedByClientInProgress"}
          statusCollectionRef={statusCollectionRef.current}
          notifyParent={forceUpdate}
        />
      </AddButtonCv>
    </StatusCvContainer>
  );

  const CvInterviewedByClientCellMemoizedRenderer = React.memo(CvInterviewedByClient, (prevProps, nextProps) => {
    return (
      prevProps?.cVsInterviewedByClientInProgress === nextProps?.cVsInterviewedByClientInProgress &&
      prevProps?.cVsInterviewedByClientDeclined === nextProps?.cVsInterviewedByClientDeclined
    );
  });

  const CvInOffer = (row: PositionDTO) => (
    <StatusCvContainer>
      {row?.cVsInOfferInProgress && row?.cVsInOfferDeclined && (
        <Container>
          <div>
            {row.cVsInOfferInProgress.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsInOfferInProgress as CandidateRef[]}
                currCollectionName={"cVsInOfferInProgress"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
          <div>
            {row.cVsInOfferDeclined.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsInOfferDeclined as CandidateRef[]}
                currCollectionName={"cVsInOfferDeclined"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  style={{ color: theme.palette.error.main }}
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
        </Container>
      )}
      <AddButtonCv>
        <EditCvStatus
          currPosition={row}
          currCollection={row.cVsInOfferInProgress as CandidateRef[]}
          currCollectionName={"cVsInOfferInProgress"}
          statusCollectionRef={statusCollectionRef.current}
          notifyParent={forceUpdate}
        />
      </AddButtonCv>
    </StatusCvContainer>
  );

  const CvInOfferCellMemoizedRenderer = React.memo(CvInOffer, (prevProps, nextProps) => {
    return (
      prevProps?.cVsInOfferInProgress === nextProps?.cVsInOfferInProgress &&
      prevProps?.cVsInOfferDeclined === nextProps?.cVsInOfferDeclined
    );
  });

  const cVsHired = (row: PositionDTO) => (
    <StatusCvContainer>
      {row?.cVsHired && row?.cVsOfferRejected && (
        <Container>
          <div>
            {row.cVsHired.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsHired as CandidateRef[]}
                currCollectionName={"cVsHired"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
          <div>
            {row.cVsOfferRejected.map((cv, idx) => (
              <EditCvStatus
                key={idx}
                candidate={cv}
                currPosition={row}
                currCollection={row.cVsOfferRejected as CandidateRef[]}
                currCollectionName={"cVsOfferRejected"}
                statusCollectionRef={statusCollectionRef.current}
                currIndex={idx}
                notifyParent={forceUpdate}
              >
                <span
                  style={{ color: theme.palette.error.main }}
                  key={cv.id}
                  title={cv.candidateCode}
                >
                  {cv.name}
                </span>
              </EditCvStatus>
            ))}
          </div>
        </Container>
      )}
      <AddButtonCv>
        <EditCvStatus
          currPosition={row}
          currCollection={row.cVsHired as CandidateRef[]}
          currCollectionName={"cVsHired"}
          statusCollectionRef={statusCollectionRef.current}
          notifyParent={forceUpdate}
        />
      </AddButtonCv>
    </StatusCvContainer>
  );

  const CvHiredCellMemoizedRenderer = React.memo(cVsHired, (prevProps, nextProps) => {
    return prevProps?.cVsHired === nextProps?.cVsHired && prevProps?.cVsOfferRejected === nextProps?.cVsOfferRejected;
  });

  const LanguageContainer = (row: PositionDTO) => (
    <SkillsBlock>
      <PositionSkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.language as SkillCategoryRef}
        notifyParent={forceUpdate}
        position={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.requiredSkills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.language.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </PositionSkillsDetailsModal>

      <AddButton>
        <AddEditRequiredPositionSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          position={row}
          skillCategory={SKILLS_CATEGORIES.language as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditRequiredPositionSkills>
      </AddButton>
    </SkillsBlock>
  );

  const LanguageCellMemoizedRenderer = React.memo(LanguageContainer, (prevProps, nextProps) => {
    return prevProps?.requiredSkills === nextProps?.requiredSkills;
  });

  const PersonalSkillsContainer = (row: PositionDTO) => (
    <SkillsBlock>
      <PositionSkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.personal as SkillCategoryRef}
        notifyParent={forceUpdate}
        position={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.requiredSkills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.personal.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </PositionSkillsDetailsModal>
      <AddButton>
        <AddEditRequiredPositionSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          position={row}
          skillCategory={SKILLS_CATEGORIES.personal as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditRequiredPositionSkills>
      </AddButton>
    </SkillsBlock>
  );

  const PersonalSkillsCellMemoizedRenderer = React.memo(PersonalSkillsContainer, (prevProps, nextProps) => {
    return prevProps?.requiredSkills === nextProps?.requiredSkills;
  });

  const TechnicalSkillsContainer = (row: PositionDTO) => (
    <SkillsBlock>
      <PositionSkillsDetailsModal
        skillCategory={SKILLS_CATEGORIES.technical as SkillCategoryRef}
        notifyParent={forceUpdate}
        position={row}
      >
        <SkillsContainer title={"Click here to edit and for more information"}>
          {row?.requiredSkills
            ?.filter((currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.technical.id)
            .map((currSkill) => currSkill?.skill?.name)
            .join(", ")}
        </SkillsContainer>
      </PositionSkillsDetailsModal>
      <AddButton>
        <AddEditRequiredPositionSkills
          effect={"add"}
          handleNotifyParent={forceUpdate}
          position={row}
          skillCategory={SKILLS_CATEGORIES.technical as SkillCategoryRef}
        >
          <Button>
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </AddEditRequiredPositionSkills>
      </AddButton>
    </SkillsBlock>
  );

  const TechnicalSkillsCellMemoizedRenderer = React.memo(TechnicalSkillsContainer, (prevProps, nextProps) => {
    return prevProps?.requiredSkills === nextProps?.requiredSkills;
  });

  const columns: GridColumns = [
    {
      field: "position",
      renderCell: ({ row }) => <PositionCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.position`)} | ${t(`${translationFields}.position_edit`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.position`)} | ${t(
            `${translationFields}.position_edit`
          )}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },

    {
      field: "hiringClient",
      renderCell: ({ row }) => <HiringClientCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.hiring_client`)} | ${t(`${translationFields}.client_contact`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.hiring_client`)} | ${t(
            `${translationFields}.client_contact`
          )}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 200,
    },

    {
      field: "isOpen",
      renderCell: ({ row }) => <DateStatusCellMemoizedRenderer {...row} />,
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.status`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.status`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      align: "center",
    },

    {
      field: "activeRecruiters",
      valueGetter: ({ row }) => row?.activeRecruiters?.map((recruiter: RecruiterRef) => recruiter.name).join(", "),
      headerName: t(`${translationFields}.active_recruiters`),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: renderCellExpand,
    },

    {
      field: "countries",
      valueGetter: ({ row }) => row?.countries?.map((country: CountryRef) => country.name).join(", "),
      headerName: t(`${translationFields}.countries`),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderCellExpand,
    },

    {
      field: "cVsSentToClient",
      renderCell: ({ row }) => <SentCvToClientCellMemoizedRenderer {...row} />,
      headerName: t(`${translationFields}.cv_sent_to_client`),
      align: "left",
      cellClassName: (params) => {
        return params.field === "cVsSentToClient" ? "flexStart" : "";
      },
      headerAlign: "center",
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsInterviewdByClient",
      renderCell: ({ row }) => <CvInterviewedByClientCellMemoizedRenderer {...row} />,
      headerName: t(`${translationFields}.cv_interviewed_by_client`),
      align: "left",
      cellClassName: (params) => {
        return params.field === "cVsInterviewdByClient" ? "flexStart" : "";
      },
      headerAlign: "center",
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsInOffer",
      renderCell: ({ row }) => <CvInOfferCellMemoizedRenderer {...row} />,
      headerName: t(`${translationFields}.cv_in_offer`),
      align: "left",
      cellClassName: (params) => {
        return params.field === "cVsInOffer" ? "flexStart" : "";
      },
      headerAlign: "center",
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
    },

    {
      field: "cVsHired",
      renderCell: ({ row }) => <CvHiredCellMemoizedRenderer {...row} />,
      headerName: t(`${translationFields}.cv_hired`),
      align: "left",
      cellClassName: (params) => {
        return params.field === "cVsHired" ? "flexStart" : "";
      },
      headerAlign: "center",
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
    },

    {
      field: "language",
      headerName: "Language skills",
      renderCell: ({ row }) => <LanguageCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },

    {
      field: "personal",
      headerName: "Personal skills",
      renderCell: ({ row }) => <PersonalSkillsCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },

    {
      field: "technical",
      headerName: "Technical skills",
      renderCell: ({ row }) => <TechnicalSkillsCellMemoizedRenderer {...row} />,

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      sortable: false,
      filterable: false,
    },
  ];

  const formFields: FormField[] = [
    {
      labelKey: t(`${translationFields}.code`),
      field: "positionCode",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.hiring_client`),
      field: "hiringClient",
      fieldType: "dropdown",
      apiService: new HiringClientsApiService(),
    },
    {
      labelKey: t(`${translationFields}.position_edit`),
      field: "position",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.open_until`),
      field: "openUntil",
      fieldType: "datePicker",
    },
    {
      labelKey: t(`${translationFields}.is_open`),
      field: "isOpen",
      fieldType: "checkbox",
    },
    {
      labelKey: t(`${translationFields}.client_contact`),
      field: "clientContact",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.active_recruiters`),
      field: "activeRecruiters",
      fieldType: "dropdown",
      isMultiSelect: true,
      apiService: new RecruitersApiService(),
    },
    {
      labelKey: t(`${translationFields}.countries`),
      field: "countries",
      fieldType: "dropdown",
      apiService: new CountriesApiService(),
      isMultiSelect: true,
    },
    {
      labelKey: t(`${translationFields}.description`),
      field: "description",
      fieldType: "textarea",
    },
  ];

  const defaultItem: Partial<PositionDTO> = {
    positionCode: "",
    hiringClient: null,
    position: "",
    openUntil: futureDay,
    activeRecruiters: [],
    clientContact: "",
    countries: [],
    isOpen: true,
    description: "",
    cVsSentToClientInProgress: [],
    cVsSentToClientDeclined: [],
    cVsInterviewedByClientInProgress: [],
    cVsInterviewedByClientDeclined: [],
    cVsInOfferInProgress: [],
    cVsInOfferDeclined: [],
    cVsHired: [],
    cVsOfferRejected: [],
    requiredSkills: [],
  };

  return (
    <>
      {isAllowedEditAddDelete && (
        <Table
          translationRootPath={tRP}
          columns={columns}
          serviceApi={new PositionsApiService()}
          formFields={formFields}
          defaultItem={defaultItem}
          filter={FT_PT}
          readOnly={!isAllowedEditAddDelete}
          propsFilter={
            {
              isOpen: statusQuickFilter.statuses.isTrueOrFalse,
              fromMonthsAgo: positionDateQuickFilter.positionDate.fromMonthsAgo,
              daysWithoutActivity: statusQuickFilter.statuses.isTrueOrFalse
                ? positionWithoutActivityQuickFilter.positionWithoutActivity.daysWithoutActivity
                : null,
            } as PropsFilter
          }
          quickFilters={
            <QuickfiltersContainer>
              <FiltersContainer>
                <StatusQuickfilter
                  quickFilters={statusQuickFilter}
                  handleChangeQuickFilters={setStatusQuickFilter}
                  tab={"positions"}
                />
                <PositionDateQuickfilter
                  quickFilters={positionDateQuickFilter}
                  handleChangeQuickFilters={setPositionDateQuickFilter}
                />
                {statusQuickFilter.statuses.isTrueOrFalse && (
                  <PositionWithoutActivityQuickfilter
                    quickFilters={positionWithoutActivityQuickFilter}
                    handleChangeQuickFilters={setPositionWithoutActivityQuickFilter}
                  />
                )}
              </FiltersContainer>
            </QuickfiltersContainer>
          }
        />
      )}
    </>
  );
};

export default Positions;
